import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Table, Form, InputGroup, Badge, Modal, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const UsersList = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for user edit modal
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    role: '',
    isActive: true,
    password: ''
  });
  
  // State for password reset
  const [resetInProgress, setResetInProgress] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(null);
  
  const { token } = useContext(AuthContext);
  
  // Fetch customers when component mounts
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch('/api/admin/customers', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch customers');
        }
        
        const data = await response.json();
        setCustomers(data);
        
        // Select the first customer by default
        if (data.length > 0) {
          setSelectedCustomer(data[0]);
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
        toast.error('Failed to load customers');
      }
    };
    
    fetchCustomers();
  }, [token]);
  
  // Fetch users when selected customer changes
  useEffect(() => {
    const fetchUsers = async () => {
      if (!selectedCustomer) return;
      
      setIsLoading(true);
      
      try {
        const response = await fetch(`/api/admin/${selectedCustomer.CompanyName}/users`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        
        const data = await response.json();
        setUsers(data);
        setFilteredUsers(data);
      } catch (err) {
        console.error('Error fetching users:', err);
        toast.error('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsers();
  }, [selectedCustomer, token]);
  
  // Filter users based on search term
  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = users.filter(
        user => 
          user.Username.toLowerCase().includes(term) ||
          user.Email.toLowerCase().includes(term) ||
          (user.FirstName && user.FirstName.toLowerCase().includes(term)) ||
          (user.LastName && user.LastName.toLowerCase().includes(term))
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleCustomerChange = (e) => {
    const customerId = parseInt(e.target.value);
    const customer = customers.find(c => c.CustomerId === customerId);
    setSelectedCustomer(customer);
  };
  
  // Open edit modal for a user
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      username: user.Username,
      email: user.Email,
      firstName: user.FirstName || '',
      lastName: user.LastName || '',
      role: user.Role,
      isActive: user.IsActive,
      password: '' // Empty password means it won't be updated
    });
    setShowModal(true);
  };
  
  // Handle form input changes
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: value
    });
  };
  
  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setEditForm({
      ...editForm,
      [name]: checked
    });
  };
  
  // Handle password reset request
  const handlePasswordReset = async (user) => {
    if (!selectedCustomer) return;
    
    setResetInProgress(true);
    setResetSuccess(null);
    
    try {
      const response = await fetch('/api/password-reset/admin/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          userId: user.UserId,
          customerName: selectedCustomer.CompanyName
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to send password reset email');
      }
      
      const data = await response.json();
      setResetSuccess(user.UserId);
      toast.success('Password reset email sent successfully');
      
      // Clear the success indicator after 3 seconds
      setTimeout(() => {
        setResetSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error sending password reset:', err);
      toast.error(err.message || 'Failed to send password reset email');
    } finally {
      setResetInProgress(false);
    }
  };
  
  // Update user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!selectedCustomer || !selectedUser) return;
    
    try {
      const response = await fetch(`/api/admin/users/${selectedUser.UserId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          username: editForm.username,
          email: editForm.email,
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          role: editForm.role,
          isActive: editForm.isActive,
          password: editForm.password || undefined, // Only send password if it's not empty
          customerName: selectedCustomer.CompanyName
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update user');
      }
      
      const updatedUser = await response.json();
      
      // Update users state
      setUsers(users.map(user => 
        user.UserId === selectedUser.UserId ? {
          ...user,
          Username: updatedUser.Username,
          Email: updatedUser.Email,
          FirstName: updatedUser.FirstName,
          LastName: updatedUser.LastName,
          Role: updatedUser.Role,
          IsActive: updatedUser.IsActive
        } : user
      ));
      
      setShowModal(false);
      toast.success('User updated successfully');
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error('Failed to update user');
    }
  };
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Users Management</h2>
        <Button as={Link} to="/admin/users/create" variant="primary">
          Create User
        </Button>
      </div>
      
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label>Select Customer</Form.Label>
                <Form.Select
                  value={selectedCustomer?.CustomerId || ''}
                  onChange={handleCustomerChange}
                >
                  {customers.map(customer => (
                    <option key={customer.CustomerId} value={customer.CustomerId}>
                      {customer.CompanyName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Search Users</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder="Search by name, email, etc."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-4">Loading users...</div>
          ) : (
            <>
              {filteredUsers.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(user => (
                        <tr key={user.UserId}>
                          <td>{user.Username}</td>
                          <td>{`${user.FirstName || ''} ${user.LastName || ''}`}</td>
                          <td>{user.Email}</td>
                          <td>
                            <Badge bg={user.Role === 'admin' ? 'danger' : 'primary'}>
                              {user.Role}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={user.IsActive ? 'success' : 'secondary'}>
                              {user.IsActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex">
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditUser(user)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handlePasswordReset(user)}
                                disabled={resetInProgress}
                                className={resetSuccess === user.UserId ? 'bg-success text-white' : ''}
                              >
                                {resetInProgress && resetSuccess === user.UserId ? (
                                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                ) : null}
                                {resetSuccess === user.UserId ? 'Email Sent!' : 'Reset Password'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-center">
                  {users.length > 0 
                    ? 'No users match your search criteria.' 
                    : 'No users found for this customer.'}
                </p>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      
      {/* Edit User Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateUser}>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={editForm.username}
                onChange={handleEditFormChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editForm.email}
                onChange={handleEditFormChange}
                required
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={editForm.firstName}
                    onChange={handleEditFormChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={editForm.lastName}
                    onChange={handleEditFormChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                name="role"
                value={editForm.role}
                onChange={handleEditFormChange}
                required
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                name="isActive"
                checked={editForm.isActive}
                onChange={handleCheckboxChange}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Password (leave blank to keep unchanged)</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={editForm.password}
                onChange={handleEditFormChange}
                placeholder="Enter new password"
              />
              <Form.Text className="text-muted">
                Minimum 6 characters if changing
              </Form.Text>
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default UsersList;
